import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'src/hooks/use-translation';

import { useAuthContext } from 'src/auth/hooks';
import { useActivePlatform } from 'src/hooks/use-active-platform';
import { useTariff } from 'src/hooks/use-tariff';
import type { AiChat, AiChatDetail } from 'src/types';

import { trackTaskCompleted, type TurnOutcome } from '../analytics';
import { CONTEXT_WINDOW_TOKENS } from '../config';
import { runAgentTurn, type EngineEvent } from '../core/engine';
import { ContextOverflowError, type ChatMessage } from '../core/llm';
import { parseTranscript } from '../history/transcript-codec';
import { useChatPersistence } from '../history/use-chat-persistence';
import { createPrivacyGateway, type PrivacyGateway } from '../privacy/gateway';
import {
  AiOrchestratorContext,
  type ChatItem,
  type OrchestratorPhase,
} from './ai-orchestrator-context';
import { buildPageContext } from './page-context';
import { useBoolean } from 'src/hooks/use-boolean';

// ----------------------------------------------------------------------

let itemCounter = 0;
const nextId = () => {
  itemCounter += 1;
  return `ai-item-${itemCounter}`;
};

type Props = {
  children: React.ReactNode;
};

/** Per-turn analytics counters, reset at the start of every send. */
type TurnStatsRef = {
  startedAt: number;
  toolCalls: number;
  toolErrors: number;
  confirmations: number;
  outcome: TurnOutcome | null;
};

const emptyTurnStats = (): TurnStatsRef => ({
  startedAt: 0,
  toolCalls: 0,
  toolErrors: 0,
  confirmations: 0,
  outcome: null,
});

// The toolsets (and every API module they bind) load with the first message instead of with
// every page. `registerAllToolsets` is idempotent, and the import resolves once.
const loadToolsets = () =>
  import('../toolsets').then(({ registerAllToolsets }) => registerAllToolsets());

export const AiOrchestratorProvider = ({ children }: Props) => {
  const { i18n } = useTranslation();
  const { platform, permissionMatrix, userRole, features } = useActivePlatform();
  const { canUseFeature } = useTariff();
  const { user } = useAuthContext();
  const queryClient = useQueryClient();
  const { mutateAsync: saveChat } = useChatPersistence();

  const { value: isOpen, onTrue: open, onFalse: close } = useBoolean();
  const [items, setItems] = useState<ChatItem[]>([]);
  const [phase, setPhase] = useState<OrchestratorPhase>('idle');
  const [contextTokens, setContextTokens] = useState(0);

  const historyRef = useRef<ChatMessage[]>([]);
  const loadedToolsRef = useRef<Set<string>>(new Set());
  const privacyRef = useRef<PrivacyGateway>(createPrivacyGateway());
  const abortRef = useRef<AbortController | null>(null);
  const confirmResolverRef = useRef<((approved: boolean) => void) | null>(null);
  const turnStatsRef = useRef<TurnStatsRef>(emptyTurnStats());
  const turnIndexRef = useRef(0);
  const activeChatIdRef = useRef<AiChat['id'] | null>(null);

  const itemsRef = useRef<ChatItem[]>([]);

  const saveChainRef = useRef<Promise<void>>(Promise.resolve());

  const conversationRef = useRef(0);

  const [activeChatId, setActiveChatId] = useState<AiChat['id'] | null>(null);

  const updateItems = useCallback((updater: (current: ChatItem[]) => ChatItem[]) => {
    itemsRef.current = updater(itemsRef.current);
    setItems(itemsRef.current);
  }, []);

  const patchItem = useCallback(
    (id: string, patch: Partial<ChatItem>) => {
      updateItems((current) =>
        current.map((item) => (item.id === id ? ({ ...item, ...patch } as ChatItem) : item))
      );
    },
    [updateItems]
  );

  const handleEvent = useCallback(
    (event: EngineEvent) => {
      switch (event.type) {
        case 'phase':
          setPhase(event.phase);
          break;
        case 'tools-loaded':
          updateItems((current) => [
            ...current,
            { kind: 'tools-loaded', id: nextId(), names: event.names },
          ]);
          break;
        case 'tool-start':
          updateItems((current) => [
            ...current,
            { kind: 'tool', id: event.callId, toolName: event.toolName, status: 'running' },
          ]);
          break;
        case 'tool-end':
          turnStatsRef.current.toolCalls += 1;
          if (event.status === 'error') {
            turnStatsRef.current.toolErrors += 1;
          }
          updateItems((current) => {
            const exists = current.some((item) => item.id === event.callId);
            // Denied/rejected calls never emitted tool-start — add the row here.
            if (!exists) {
              return [
                ...current,
                { kind: 'tool', id: event.callId, toolName: event.toolName, status: event.status },
              ];
            }
            return current.map((item) =>
              item.id === event.callId && item.kind === 'tool'
                ? { ...item, status: event.status }
                : item
            );
          });
          break;
        case 'tool-attachment':
          updateItems((current) => [
            ...current,
            { kind: 'image', id: `${event.callId}-image`, path: event.attachment.path },
          ]);
          break;
        case 'assistant-message':
          if (event.text) {
            updateItems((current) => [
              ...current,
              { kind: 'assistant', id: nextId(), text: event.text },
            ]);
          }
          break;
        case 'context-usage':
          setContextTokens(event.totalTokens);
          break;
        case 'turn-end':
          turnStatsRef.current.outcome = event.outcome;
          break;
        default:
          break;
      }
    },
    [updateItems]
  );

  const requestConfirmation = useCallback(
    (request: { toolName: string; args: unknown }): Promise<boolean> =>
      new Promise<boolean>((resolve) => {
        turnStatsRef.current.confirmations += 1;
        const id = nextId();
        confirmResolverRef.current = (approved: boolean) => {
          confirmResolverRef.current = null;
          patchItem(id, { state: approved ? 'approved' : 'rejected' });
          setPhase('thinking');
          resolve(approved);
        };
        updateItems((current) => [
          ...current,
          {
            kind: 'confirmation',
            id,
            toolName: request.toolName,
            args: request.args,
            state: 'pending',
          },
        ]);
        setPhase('awaiting-confirmation');
      }),
    [patchItem, updateItems]
  );

  const resolveConfirmation = useCallback((itemId: string, approved: boolean) => {
    void itemId;
    confirmResolverRef.current?.(approved);
  }, []);

  const handleMutationSuccess = useCallback(
    (keys?: readonly (readonly unknown[])[]) => {
      if (!keys) {
        // No precise mapping declared — refresh everything to stay correct.
        queryClient.invalidateQueries();
        return;
      }
      keys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
    },
    [queryClient]
  );

  const persistTurn = useCallback(
    (history: ChatMessage[]) => {
      const items = itemsRef.current;
      const conversation = conversationRef.current;

      saveChainRef.current = saveChainRef.current
        .then(async () => {
          if (conversation !== conversationRef.current) return;

          await saveChat({
            chatId: activeChatIdRef.current,
            conversation: { items, history, vault: privacyRef.current.serialize() },
            onSaved: (chatId) => {
              if (conversation !== conversationRef.current) return;

              activeChatIdRef.current = chatId;
              setActiveChatId(chatId);
            },
          });
        })
        .catch((error: unknown) => {
          console.warn('[ai-orchestrator] chat history save failed', error);
        });
    },
    [saveChat]
  );

  const isContextFull = contextTokens >= CONTEXT_WINDOW_TOKENS;

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || phase !== 'idle' || isContextFull) return;

      const controller = new AbortController();
      abortRef.current = controller;

      const conversation = conversationRef.current;
      const isStale = () => conversation !== conversationRef.current;

      turnStatsRef.current = { ...emptyTurnStats(), startedAt: Date.now() };
      turnIndexRef.current += 1;

      updateItems((current) => [...current, { kind: 'user', id: nextId(), text: trimmed }]);
      setPhase('planning');

      loadToolsets()
        .then(() => {
          controller.signal.throwIfAborted();

          return runAgentTurn({
            history: historyRef.current,
            userText: trimmed,
            ctx: {
              platformId: platform?.id ?? null,
              platformName: platform?.name,
              userRole,
              userName: user ? `${user.first_name} ${user.last_name}`.trim() : undefined,
              appLanguage: i18n.language,
              permissionMatrix,
              canUseTariffFeature: canUseFeature,
              features,
              pageContext: buildPageContext(),
              loadedTools: loadedToolsRef.current,
              privacy: privacyRef.current,
              signal: controller.signal,
              onEvent: handleEvent,
              requestConfirmation,
              onMutationSuccess: handleMutationSuccess,
            },
          });
        })
        .then((updatedHistory) => {
          if (isStale()) return;

          historyRef.current = updatedHistory;
          persistTurn(updatedHistory);

          const stats = turnStatsRef.current;
          if (stats.outcome) {
            trackTaskCompleted(
              { platformId: platform?.id ?? null, userRole },
              {
                outcome: stats.outcome,
                toolCalls: stats.toolCalls,
                toolErrors: stats.toolErrors,
                confirmations: stats.confirmations,
                turnIndex: turnIndexRef.current,
                durationMs: Date.now() - stats.startedAt,
              }
            );
          }
        })
        .catch((error: unknown) => {
          if (isStale()) return;

          const aborted = controller.signal.aborted;
          const contextFull = !aborted && error instanceof ContextOverflowError;

          if (contextFull) {
            setContextTokens(CONTEXT_WINDOW_TOKENS);
          }

          const messageKey = (() => {
            if (aborted) return 'aborted' as const;
            return contextFull ? ('context-full' as const) : ('generic' as const);
          })();

          updateItems((current) => [...current, { kind: 'error', id: nextId(), messageKey }]);

          if (!aborted && !contextFull) {
            console.error('[ai-orchestrator]', error);
          }
        })
        .finally(() => {
          if (abortRef.current === controller) {
            abortRef.current = null;
          }
          if (isStale()) return;

          setPhase('idle');
        });
    },
    [
      phase,
      isContextFull,
      platform,
      userRole,
      user,
      i18n.language,
      permissionMatrix,
      canUseFeature,
      features,
      handleEvent,
      requestConfirmation,
      handleMutationSuccess,
      updateItems,
      persistTurn,
    ]
  );

  const stop = useCallback(() => {
    confirmResolverRef.current?.(false);
    abortRef.current?.abort();
  }, []);

  const startNewChat = useCallback(() => {
    stop();
    updateItems(() => []);
    historyRef.current = [];
    loadedToolsRef.current = new Set();
    privacyRef.current = createPrivacyGateway();
    turnIndexRef.current = 0;
    activeChatIdRef.current = null;
    conversationRef.current += 1;
    setActiveChatId(null);
    setContextTokens(0);
    setPhase('idle');
  }, [stop, updateItems]);

  const restoreChat = useCallback(
    (chat: AiChatDetail) => {
      const conversation = parseTranscript(chat);
      if (!conversation) return false;

      stop();
      updateItems(() => conversation.items);
      privacyRef.current = createPrivacyGateway(conversation.vault);
      historyRef.current = privacyRef.current.maskHistory(conversation.history);
      loadedToolsRef.current = new Set();
      turnIndexRef.current = 0;
      activeChatIdRef.current = chat.id;
      conversationRef.current += 1;
      setActiveChatId(chat.id);
      setContextTokens(0);
      setPhase('idle');
      return true;
    },
    [stop, updateItems]
  );

  const value = useMemo(
    () => ({
      isOpen,
      open,
      close,
      items,
      phase,
      contextUsage: { used: contextTokens, max: CONTEXT_WINDOW_TOKENS },
      isContextFull,
      activeChatId,
      send,
      stop,
      startNewChat,
      restoreChat,
      resolveConfirmation,
    }),
    [
      isOpen,
      open,
      close,
      items,
      phase,
      contextTokens,
      isContextFull,
      activeChatId,
      send,
      stop,
      startNewChat,
      restoreChat,
      resolveConfirmation,
    ]
  );

  return <AiOrchestratorContext.Provider value={value}>{children}</AiOrchestratorContext.Provider>;
};
